import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import IosInstallSheet from "@/components/IosInstallSheet";
import { isIOSDevice, isIOSSafari, trackEvent } from "@/lib/platform";
import { isStandaloneDisplay } from "@/lib/standalone";

const KEY = "tl-ios-install-nudge-v1";
const COOLDOWN_MS = 21 * 24 * 60 * 60 * 1000; // 21 days after a dismissal

function suppressed(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { dismissedAt?: number; done?: boolean };
    if (data.done) return true;
    return !!data.dismissedAt && Date.now() - data.dismissedAt < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function remember(patch: { dismissedAt?: number; done?: boolean }) {
  try {
    localStorage.setItem(KEY, JSON.stringify(patch));
  } catch {
    /* private mode */
  }
}

/**
 * Discreet, one-at-a-time "add to Home Screen" suggestion for iOS Safari users
 * who are not running the installed app.
 *
 * It never shows on page load: it waits for an intent signal dispatched by the
 * app (`tl:ios-install-intent`, fired after a successful match save / sign-in).
 * Dismissal is persisted with a cooldown, so users are not nagged.
 */
export default function IosInstallNudge() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!isIOSDevice()) return;
    if (isStandaloneDisplay()) {
      trackEvent("ios_standalone_detected");
      remember({ done: true });
      return;
    }
    if (!isIOSSafari()) return; // in-app browsers can't add to Home Screen

    const onIntent = () => {
      if (suppressed()) return;
      setVisible(true);
      trackEvent("ios_install_prompt_shown");
    };
    window.addEventListener("tl:ios-install-intent", onIntent);
    return () => window.removeEventListener("tl:ios-install-intent", onIntent);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    remember({ dismissedAt: Date.now() });
    setVisible(false);
    trackEvent("ios_install_prompt_dismissed");
  };

  return (
    <>
      <div
        role="dialog"
        aria-label={t("install.iosNudgeTitle")}
        className="fixed inset-x-3 z-50 rounded-2xl border border-primary/30 bg-card/95 backdrop-blur-xl shadow-glow p-4 animate-fade-in"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("install.iosNudgeDismiss")}
          className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <span className="flex items-center justify-center w-9 h-9 shrink-0 rounded-xl bg-primary/15 text-primary">
            <Smartphone className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-sm">{t("install.iosNudgeTitle")}</p>
            <p className="text-xs text-muted-foreground leading-snug mt-0.5">
              {t("install.iosNudgeBody")}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            className="flex-1 bg-gradient-primary text-primary-foreground"
            onClick={() => setSheetOpen(true)}
          >
            {t("install.iosNudgeShowMe")}
          </Button>
          <Button size="sm" variant="ghost" className="flex-1" onClick={dismiss}>
            {t("install.iosNudgeDismiss")}
          </Button>
        </div>
      </div>
      <IosInstallSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) {
            remember({ dismissedAt: Date.now() });
            setVisible(false);
          }
        }}
        source="nudge"
      />
    </>
  );
}
